import React, { useId, useMemo, useState } from 'react';
import { HwDialog } from '../../views/HomeView/widgets/HwDialog';
import { useNutritionStore } from '../../../stores/nutritionStore';
import { readableOn } from '../../../utils/colorText';
import {
  type Meal, type NutrientKey, type Nutrients, type OccasionType,
  NUTRIENT_KEYS, parseNutrient, scaleNutrients,
} from '../../../utils/nutrition';
import { addLabel } from './nutritionFormat';
import { foodIcon } from './foodIcons';
import { NutrientField, NutrientLine, PortionStepper, SaveError } from './nutritionUi';

type Tab = 'meals' | 'oneoff';

/**
 * ⭐ Add food (Nutrition phase 1) — a dialog on desktop, a bottom sheet on a phone
 * (HwDialog). Two ways in: FROM YOUR MEALS (search, pick, portion) or a ONE-OFF FOOD
 * (a name; every nutrient optional — blank = unknown, 0 = none), which can also be
 * saved as a meal. Logs onto the day the caller passes; the store refuses a day the
 * back-dating rule doesn't allow, and a refused save keeps the dialog open with the
 * reason — nothing claims success.
 */
export function AddFoodDialog({ date, dateLabel, color, initialMeal, initialPortion = 1, occasion, logOptions, onClose, onLogged }: {
  date: string;
  dateLabel: string;
  color: string;
  initialMeal?: Meal;
  initialPortion?: number;
  /** Logging for a planned occasion (phase 3a "Change"): the entry is stamped with it. */
  occasion?: { id: string; name: string; kind?: OccasionType | null };
  /** A linked eat-and-tick (phase 3b-2): the shared operation id, and the habit's day rule. */
  logOptions?: { opId?: string; habitAllowsDate?: boolean };
  onClose: () => void;
  onLogged?: (entryId: string, name: string) => void;
}) {
  const meals = useNutritionStore(s => s.meals);
  const mealsFailed = useNutritionStore(s => s.mealsFailed);
  const reloadMeals = useNutritionStore(s => s.reloadMeals);
  const logFood = useNutritionStore(s => s.logFood);
  const createMeal = useNutritionStore(s => s.createMeal);
  const idp = useId();

  const active = useMemo(() => meals.filter(m => !m.archivedAt), [meals]);
  const [tab, setTab] = useState<Tab>(initialMeal || active.length ? 'meals' : 'oneoff');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(initialMeal?.id ?? null);
  const [portion, setPortion] = useState(initialPortion);
  const [name, setName] = useState('');
  const [vals, setVals] = useState<Record<NutrientKey, string>>(() => Object.fromEntries(NUTRIENT_KEYS.map(k => [k, ''])) as Record<NutrientKey, string>);
  const [saveAsMeal, setSaveAsMeal] = useState(false);
  const [serving, setServing] = useState('1 serving');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* "Save as a meal": once the meal is saved, a retry after a failed LOG reuses it —
     never a second copy of the same meal. */
  const [createdMeal, setCreatedMeal] = useState<Meal | null>(null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? active.filter(m => m.name.toLowerCase().includes(q)) : active;
  }, [active, query]);
  const meal = active.find(m => m.id === picked) ?? null;
  const eaten = meal ? scaleNutrients(meal.nutrients, portion) : null;

  const oneOffNutrients = (): Nutrients => {
    const n = {} as Nutrients;
    for (const k of NUTRIENT_KEYS) n[k] = parseNutrient(k, vals[k]);
    return n;
  };

  // Only a linked eat-and-tick passes options; a plain log keeps its two-argument call.
  const opts = (logOptions ? [logOptions] : []) as [typeof logOptions?];
  const submit = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    let r;
    if (tab === 'meals') {
      if (!meal) { setBusy(false); return; }
      r = await logFood(date, { meal, portion, occasionId: occasion?.id }, ...opts);
    } else {
      if (!name.trim()) { setBusy(false); return; }
      if (saveAsMeal) {
        let m = createdMeal;
        if (!m) {
          // Logged for a typed occasion ("Lunch") → that's what it was for; otherwise it stays
          // untagged and the Meals list asks (logging stays one step).
          const created = await createMeal({
            name, servingLabel: serving, servingGrams: null, nutrients: oneOffNutrients(), source: 'user', icon: 'utensils',
            ...(occasion?.kind ? { occasionTags: [occasion.kind] } : {}),
          });
          if (!created.ok) { setBusy(false); setError(created.error); return; }
          m = created.value;
          setCreatedMeal(m);
        }
        r = await logFood(date, { meal: m, portion: 1, occasionId: occasion?.id }, ...opts);
      } else {
        r = await logFood(date, { name, nutrients: oneOffNutrients(), occasionId: occasion?.id }, ...opts);
      }
    }
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onLogged?.(r.value.id, r.value.name);
    onClose();
  };

  const ink = readableOn(color);
  const canSubmit = tab === 'meals' ? !!meal && portion > 0 : name.trim().length > 0 && (!saveAsMeal || serving.trim().length > 0);
  const primary = tab === 'meals'
    ? (meal ? addLabel(portion, eaten?.calories ?? null) : 'Choose a meal')
    : saveAsMeal ? 'Save meal and add' : 'Add food';
  const tabBtn = (t: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === t}
      onClick={() => { setTab(t); setError(null); }}
      className={`min-h-[44px] sm:min-h-0 pb-2 -mb-px text-sm border-b-2 ${tab === t ? 'font-semibold text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
      style={tab === t ? { borderColor: color } : undefined}
    >
      {label}
    </button>
  );

  return (
    <HwDialog
      title="Add food"
      subtitle={occasion ? `${dateLabel} · for ${occasion.name}` : dateLabel}
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className="h-10 sm:h-9 px-3 rounded-lg border border-border text-sm font-semibold text-foreground">Cancel</button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit || busy}
            className="h-10 sm:h-9 px-3 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: color, color: ink }}
          >
            {busy ? 'Saving…' : createdMeal && tab === 'oneoff' ? 'Add food' : primary}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <div role="tablist" aria-label="Add from" className="flex gap-5 border-b border-border">
          {tabBtn('meals', 'Your meals')}
          {tabBtn('oneoff', 'One-off food')}
        </div>

        {tab === 'meals' ? (
          mealsFailed ? (
            <SaveError message="Couldn’t load your meals." onRetry={() => void reloadMeals()} />
          ) : active.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              No meals yet. Add a <button type="button" className="font-semibold underline underline-offset-2" onClick={() => setTab('oneoff')}>one-off food</button> — you can save it as a meal for next time.
            </div>
          ) : (
            <>
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search your meals…"
                aria-label="Search your meals"
                className="w-full h-10 sm:h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground"
              />
              <div role="radiogroup" aria-label="Meal" className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                {shown.length === 0 && <p className="text-sm text-muted-foreground">No meal matches “{query}”.</p>}
                {shown.map(m => {
                  const Icon = foodIcon(m.icon);
                  const on = m.id === picked;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => { setPicked(m.id); setPortion(1); }}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left ${on ? 'bg-muted/50' : 'border-border hover:bg-muted/40'}`}
                      style={on ? { borderColor: color } : undefined}
                    >
                      <span className="p-1.5 rounded-lg shrink-0" style={{ backgroundColor: `${color}1f` }}><Icon className="w-4 h-4" style={{ color }} aria-hidden="true" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground truncate">{m.name}</span>
                        <span className="block text-xs text-muted-foreground">{m.servingLabel} · <NutrientLine nutrients={m.nutrients} /></span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {meal && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm text-foreground">Portion <span className="text-muted-foreground">· 1 = {meal.servingLabel}</span></span>
                    <PortionStepper id={`${idp}-portion`} value={portion} onChange={setPortion} />
                  </div>
                  <p className="text-xs text-muted-foreground">You’ll log: <NutrientLine nutrients={eaten ?? {}} /></p>
                </div>
              )}
            </>
          )
        ) : (
          <>
            <label htmlFor={`${idp}-name`} className="block">
              <span className="block text-xs font-medium text-muted-foreground mb-1">Name</span>
              <input id={`${idp}-name`} value={name} maxLength={120} onChange={e => setName(e.target.value)} className="w-full h-10 sm:h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground" placeholder="e.g. Banana" />
            </label>
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">As eaten — blank = unknown, 0 = none</legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {NUTRIENT_KEYS.map(k => (
                  <NutrientField key={k} k={k} idPrefix={idp} value={vals[k]} onChange={v => setVals(s => ({ ...s, [k]: v }))} />
                ))}
              </div>
            </fieldset>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={saveAsMeal} onChange={e => setSaveAsMeal(e.target.checked)} className="w-4 h-4" />
              Save as a meal for next time
            </label>
            {saveAsMeal && (
              <label htmlFor={`${idp}-serving`} className="block">
                <span className="block text-xs font-medium text-muted-foreground mb-1">This amount is one serving of…</span>
                <input id={`${idp}-serving`} value={serving} maxLength={60} onChange={e => setServing(e.target.value)} className="w-full h-10 sm:h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground" placeholder="1 serving, 1 piece, 100 g" />
              </label>
            )}
          </>
        )}

        {error && <SaveError message={error} onRetry={() => void submit()} />}
      </div>
    </HwDialog>
  );
}
